import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Truck, PackageCheck, Image as ImageIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, StatusBadge, Empty } from "@/components/common";
import { RejectDialog } from "@/components/RejectDialog";
import { ScreenshotDialog } from "@/components/ScreenshotDialog";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const TABS = ["pending", "confirmed", "shipped", "delivered", "rejected", "all"];

export default function Orders() {
  const api = useApi();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(() => api("get", `/admin/orders${status === "all" ? "" : `?status=${status}`}`).then(setRows), [api, status]);
  useEffect(() => { load(); }, [load]);
  const act = async (path, body, msg) => {
    try { await api("post", path, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const actions = (o) => {
    if (o.status === "pending") return (<>
        <Button size="sm" data-testid={`approve-order-btn-${o.id}`} onClick={() => act(`/admin/orders/${o.id}/approve`, {}, "Payment verified")}><Check className="mr-1 h-4 w-4" />Verify</Button>
      <Button size="sm" variant="outline" data-testid={`reject-order-btn-${o.id}`} onClick={() => setRejecting(o)}><X className="h-4 w-4" /></Button>
    </>);
    if (o.status === "confirmed") return (<>
      <Button size="sm" data-testid={`ship-order-btn-${o.id}`} onClick={() => act(`/admin/orders/${o.id}/status`, { status: "shipped" }, "Marked shipped")}><Truck className="mr-1 h-4 w-4" />Shipped</Button>
      <Button size="sm" variant="outline" data-testid={`cancel-order-btn-${o.id}`} onClick={() => setRejecting(o)}><X className="h-4 w-4" /></Button>
    </>);
    if (o.status === "shipped") return <Button size="sm" data-testid={`deliver-order-btn-${o.id}`} onClick={() => act(`/admin/orders/${o.id}/status`, { status: "delivered" }, "Marked delivered")}><PackageCheck className="mr-1 h-4 w-4" />Delivered</Button>;
    return null;
  };
  return (
    <div data-testid="admin-orders-page">
      <PageHead label="Admin" title="Product orders" />
      <Panel>
        <Tabs value={status} onValueChange={setStatus} className="mb-6">
          <TabsList className="flex-wrap">{TABS.map((s) => <TabsTrigger key={s} value={s} data-testid={`orders-tab-${s}`} className="capitalize">{s}</TabsTrigger>)}</TabsList>
        </Tabs>
        {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="admin-orders-empty" text="Nothing here." /> : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-orders-table">
              <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Product</TableHead><TableHead>Payment</TableHead><TableHead>Address</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-row-${o.id}`}>
                    <TableCell><p className="text-white">{o.name}</p><p className="text-xs text-slate-500">{o.email}</p></TableCell>
                    <TableCell><p className="text-white">{o.product_name}</p><p className="font-mono text-xs text-emerald-400">{inr(o.amount)}</p>{o.activation && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-amber-400" data-testid={`order-activation-tag-${o.id}`}>Activated ID</p>}</TableCell>
                    <TableCell className="text-xs">
                      <span className="uppercase text-slate-300">{o.method}</span>
                      {o.utr && <p className="font-mono text-emerald-400">{o.utr}</p>}
                      {o.has_screenshot && <button className="mt-1 inline-flex items-center gap-1 rounded border border-[#1F293D] px-1.5 py-0.5 text-[11px] text-slate-300" data-testid={`order-proof-btn-${o.id}`} onClick={() => setViewing(o)}><ImageIcon className="h-3 w-3" />Proof</button>}
                    </TableCell>
                    <TableCell className="max-w-[220px] whitespace-pre-line text-xs text-slate-400">{o.address}</TableCell>
                    <TableCell className="text-xs">{fmtDate(o.created_at)}</TableCell>
                    <TableCell><StatusBadge status={o.status} />{o.reason && <p className="mt-1 text-xs text-red-400">{o.reason}</p>}</TableCell>
                    <TableCell className="text-right"><div className="flex justify-end gap-2">{actions(o)}</div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
      <RejectDialog open={!!rejecting} title={rejecting?.method === "wallet" ? "Cancel & refund to wallet" : "Reject order"} onClose={() => setRejecting(null)}
        onConfirm={(reason) => { act(`/admin/orders/${rejecting.id}/reject`, { reason }, "Order rejected"); setRejecting(null); }} />
      <ScreenshotDialog payment={viewing} base="/admin/orders" onClose={() => setViewing(null)} />
    </div>
  );
}
